/*
 * DocumentosCliente.js — Archivero del cliente (C-015, ver diseno/CAMBIOS.md).
 *
 *   <DocumentosMes>   modal "Documentos de septiembre 2026": solo los tipos con archivos, un botón
 *                     "Subir archivos" (varios a la vez o arrastrando) y "Clasificar N archivos".
 *   <DocumentosAnio>  pestaña Documentos de la ficha: año, búsqueda y filtro por tipo, por mes.
 *   <ArchivoFila>     nombre (abre o descarga) y menú ⋯ con Descargar, Cambiar tipo y Eliminar.
 *
 * Todo lo que habla con el servidor llega en `api` (lo arma App.js):
 *   listar(clienteId, { mes?, anio }) · subir(file, tipo, clienteId, mes, anio) · cambiarTipo(id, tipo)
 *   eliminar(id) · abrir(archivo) · descargar(archivo) · puedeEliminar(archivo)
 *   soloLectura(mes, anio) → true si no puede cambiar ese mes · avisarSoloLectura()
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Ellipsis, FileText, Search, Upload, X } from 'lucide-react';
import { avisar, avisarError, confirmar } from './Dialogo';
import { BotonIcono } from './Tooltip';
import { fechaCorta } from './estados';
import {
  GRUPOS_DOCUMENTO, TAMANO_MAXIMO, TIPOS_DOCUMENTO, agruparPorTipo, filtrarArchivos, nombreTipo,
  sugerirTipo, textoArchivos, tipoDeArchivo,
} from './documentos';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const VISIBLES_POR_TIPO = 3;
const ANIO_MINIMO = 2025;

const primerNombre = (nombre) => (nombre || '').trim().split(/\s+/)[0];

// Hay un diálogo de confirmación encima: Escape es suyo.
const hayDialogoEncima = () => !!document.querySelector('.dialogo-fondo');

function OpcionesTipo() {
  return GRUPOS_DOCUMENTO.map(g => (
    <optgroup key={g.nombre} label={g.nombre}>
      {g.tipos.map(t => <option key={t.clave} value={t.clave}>{t.nombre}</option>)}
    </optgroup>
  ));
}

export function ArchivoFila({ archivo, conTipo = false, api, onCambio }) {
  const [menu, setMenu] = useState(false);
  const [editando, setEditando] = useState(false);
  const [tipo, setTipo] = useState(tipoDeArchivo(archivo));
  const [guardando, setGuardando] = useState(false);
  const fila = useRef(null);
  const nombre = archivo.nombre_archivo;

  // El menú se cierra con clic fuera.
  useEffect(() => {
    if (!menu) return undefined;
    const fuera = (e) => { if (fila.current && !fila.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [menu]);

  const alTeclear = (e) => {
    if (e.key === 'Escape' && (menu || editando)) {
      e.stopPropagation();
      setMenu(false);
      setEditando(false);
    }
  };

  const guardarTipo = async () => {
    if (tipo === tipoDeArchivo(archivo)) { setEditando(false); return; }
    setGuardando(true);
    try {
      await api.cambiarTipo(archivo.id, tipo);
      avisar({ texto: `${nombre} ahora es ${nombreTipo(tipo)}.` });
      setEditando(false);
      onCambio && onCambio();
    } catch (err) {
      avisarError('cambiar el tipo del archivo', err);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    setMenu(false);
    const ok = await confirmar({
      nivel: 2,
      peligro: true,
      titulo: `¿Eliminar ${nombre}?`,
      cuerpo: 'No se puede deshacer.',
      accion: 'Eliminar archivo',
      accionEnCurso: 'Eliminando…',
      ejecutar: () => api.eliminar(archivo.id),
    });
    if (!ok) return;
    avisar({ texto: `Archivo ${nombre} eliminado.` });
    onCambio && onCambio();
  };

  const subidoPor = primerNombre(archivo.subido_por_nombre);
  const cuando = fechaCorta(archivo.fecha_subida);

  return (
    <div className="doc-archivo" ref={fila} onKeyDown={alTeclear}>
      <div className="doc-archivo-fila">
        {conTipo && <span className="doc-etiqueta-tipo">{nombreTipo(tipoDeArchivo(archivo))}</span>}
        <button type="button" className="doc-nombre" onClick={() => api.abrir(archivo)}>
          <FileText size={14} aria-hidden="true" />
          <span>{nombre}</span>
        </button>
        <span className="doc-meta">
          {[subidoPor && `por ${subidoPor}`, cuando].filter(Boolean).join(' · ')}
        </span>
        <span className="doc-menu-wrap">
          <BotonIcono
            className="doc-mas"
            etiqueta={`Más acciones para ${nombre}`}
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={() => { setEditando(false); setMenu(m => !m); }}
          >
            <Ellipsis size={16} aria-hidden="true" />
          </BotonIcono>
          {menu && (
            <div className="doc-menu" role="menu" aria-label={`Acciones para ${nombre}`}>
              <button type="button" role="menuitem" onClick={() => { setMenu(false); api.descargar(archivo); }}>
                Descargar
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setMenu(false); setTipo(tipoDeArchivo(archivo)); setEditando(true); }}
              >
                Cambiar tipo
              </button>
              {api.puedeEliminar(archivo) && (
                <button type="button" role="menuitem" className="peligro" onClick={eliminar}>
                  Eliminar
                </button>
              )}
            </div>
          )}
        </span>
      </div>
      {editando && (
        <div className="doc-panel">
          <label htmlFor={`doc-tipo-${archivo.id}`}>Tipo:</label>
          <select
            id={`doc-tipo-${archivo.id}`}
            value={tipo}
            onChange={e => setTipo(e.target.value)}
            autoFocus
          >
            <OpcionesTipo />
          </select>
          <button type="button" className="doc-btn-sec" onClick={() => setEditando(false)} disabled={guardando}>
            Cancelar
          </button>
          <button type="button" className="doc-btn-pri" onClick={guardarTipo} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      )}
    </div>
  );
}

// ───────────── Documentos del mes ─────────────

export function DocumentosMes({ cliente, mes, anio, api, onCerrar, onVerAnio, onCambio }) {
  const [archivos, setArchivos] = useState(null);
  const [subiendo, setSubiendo] = useState(null); // [{ file, tipo, sugerido }]
  const [progreso, setProgreso] = useState(null); // 'Subiendo 2 de 4…'
  const [expandido, setExpandido] = useState({});
  const [arrastrando, setArrastrando] = useState(false);
  const entrada = useRef(null);
  const nombreMes = `${MESES[mes - 1].toLowerCase()} ${anio}`;

  const cargar = useCallback(async () => {
    try {
      const lista = await api.listar(cliente.id, { mes, anio });
      setArchivos(lista || []);
      onCambio && onCambio(cliente.id, (lista || []).length);
    } catch (err) {
      setArchivos([]);
      avisarError('cargar los documentos', err);
    }
  }, [api, cliente.id, mes, anio, onCambio]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || hayDialogoEncima() || progreso) return;
      if (subiendo) setSubiendo(null); else onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [subiendo, progreso, onCerrar]);

  const elegir = (lista) => {
    const files = Array.from(lista || []);
    if (!files.length) return;
    if (api.soloLectura(mes, anio)) { api.avisarSoloLectura(); return; }
    const pesados = files.filter(f => f.size > TAMANO_MAXIMO);
    if (pesados.length) {
      avisar({
        tipo: 'error',
        texto: `${pesados.map(f => f.name).join(', ')} ${pesados.length === 1 ? 'pesa' : 'pesan'} más de 15 MB, que es el máximo.`,
      });
    }
    const validos = files.filter(f => f.size <= TAMANO_MAXIMO);
    if (!validos.length) return;
    setSubiendo(validos.map(file => {
      const sugerido = sugerirTipo(file.name);
      return { file, tipo: sugerido || '', sugerido };
    }));
  };

  const guardar = async () => {
    const lista = subiendo;
    const fallidos = [];
    for (let i = 0; i < lista.length; i++) {
      setProgreso(`Subiendo ${i + 1} de ${lista.length}…`);
      try {
        await api.subir(lista[i].file, lista[i].tipo, cliente.id, mes, anio);
      } catch (err) {
        console.error(`No se pudo subir ${lista[i].file.name}:`, err);
        fallidos.push(lista[i]);
      }
    }
    setProgreso(null);
    const subidos = lista.length - fallidos.length;
    if (subidos) avisar({ texto: subidos === 1 ? `Archivo ${lista[0].file.name} subido.` : `${subidos} archivos subidos.` });
    if (fallidos.length) {
      avisar({
        tipo: 'error',
        texto: `No se pudo subir ${fallidos.map(f => f.file.name).join(', ')}. Revisa tu conexión e intenta de nuevo.`,
      });
      setSubiendo(fallidos);
    } else {
      setSubiendo(null);
    }
    cargar();
  };

  const alSoltar = (e) => {
    e.preventDefault();
    setArrastrando(false);
    if (!subiendo && !progreso) elegir(e.dataTransfer.files);
  };

  const n = archivos ? archivos.length : 0;
  const faltan = subiendo ? subiendo.filter(f => !f.tipo).length : 0;

  return (
    <div className="modal-overlay" onClick={() => { if (!progreso) onCerrar(); }}>
      <div
        className={`modal modal-documentos${arrastrando ? ' arrastrando' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="doc-mes-titulo"
        onClick={e => e.stopPropagation()}
        onDragOver={e => { e.preventDefault(); if (!subiendo) setArrastrando(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setArrastrando(false); }}
        onDrop={alSoltar}
      >
        <div className="modal-header doc-cabecera">
          <div>
            <h3 id="doc-mes-titulo">
              {subiendo ? `Clasificar ${textoArchivos(subiendo.length).toLowerCase()}` : `Documentos de ${nombreMes}`}
            </h3>
            <p className="doc-sub">
              {[cliente.nombre, cliente.contador_nombre, !subiendo && archivos && textoArchivos(n).toLowerCase()]
                .filter(Boolean).join(' · ')}
            </p>
          </div>
          <BotonIcono onClick={onCerrar} etiqueta="Cerrar" disabled={!!progreso}><X size={20} aria-hidden="true" /></BotonIcono>
        </div>

        {subiendo ? (
          <>
            <div className="doc-cuerpo">
              <p className="doc-ayuda">Revisa el tipo de cada archivo. Así se encuentra después en la ficha del cliente.</p>
              {subiendo.map((f, i) => (
                <div className="doc-clasif" key={`${f.file.name}-${i}`}>
                  <span className="doc-clasif-nombre" data-tooltip={f.file.name}>{f.file.name}</span>
                  <select
                    aria-label={`Tipo de ${f.file.name}`}
                    className={f.tipo ? '' : 'falta-tipo'}
                    value={f.tipo}
                    disabled={!!progreso}
                    onChange={e => setSubiendo(prev => prev.map((x, j) => (j === i ? { ...x, tipo: e.target.value } : x)))}
                  >
                    <option value="" disabled>Elige el tipo…</option>
                    <OpcionesTipo />
                  </select>
                  <span className="doc-pista">
                    {f.sugerido && f.tipo === f.sugerido ? 'Sugerido por el nombre'
                      : !f.tipo ? 'Sin pista en el nombre: elige el tipo' : ''}
                  </span>
                </div>
              ))}
            </div>
            <div className="doc-pie">
              <button type="button" className="doc-btn-sec" onClick={() => setSubiendo(null)} disabled={!!progreso}>
                Cancelar
              </button>
              <span className="doc-nota" role="status">
                {progreso || (faltan ? `Falta elegir el tipo de ${textoArchivos(faltan).toLowerCase()}.` : '')}
              </span>
              <button type="button" className="doc-btn-pri" onClick={guardar} disabled={faltan > 0 || !!progreso}>
                Guardar {textoArchivos(subiendo.length).toLowerCase()}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="doc-barra-subir">
              <button type="button" className="doc-btn-pri" onClick={() => entrada.current && entrada.current.click()}>
                <Upload size={16} aria-hidden="true" /> Subir archivos
              </button>
              <span>o arrástralos aquí · varios a la vez · PDF, Excel, Word, XML, ZIP o imágenes (máx. 15 MB)</span>
              <input
                ref={entrada}
                type="file"
                multiple
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.xml,.zip,.jpg,.jpeg,.png,.gif,.webp"
                onChange={e => { elegir(e.target.files); e.target.value = ''; }}
              />
            </div>
            <div className="doc-cuerpo">
              {archivos === null ? (
                <p className="doc-vacio">Cargando…</p>
              ) : n === 0 ? (
                <p className="doc-vacio">Todavía no hay archivos de este mes.</p>
              ) : (
                agruparPorTipo(archivos).map(g => {
                  const abierto = expandido[g.clave];
                  const visibles = abierto ? g.archivos : g.archivos.slice(0, VISIBLES_POR_TIPO);
                  return (
                    <section className="doc-tipo" key={g.clave}>
                      <h4>{g.nombre} <span className="doc-cuenta">{g.archivos.length}</span></h4>
                      {visibles.map(a => <ArchivoFila key={a.id} archivo={a} api={api} onCambio={cargar} />)}
                      {g.archivos.length > VISIBLES_POR_TIPO && (
                        <button
                          type="button"
                          className="doc-ver-mas"
                          onClick={() => setExpandido(prev => ({ ...prev, [g.clave]: !abierto }))}
                        >
                          {abierto ? 'Ver menos' : `Ver ${g.archivos.length - VISIBLES_POR_TIPO} más`}
                        </button>
                      )}
                    </section>
                  );
                })
              )}
            </div>
            <div className="doc-pie">
              <span className="doc-nota">Para ver un tipo de documento en varios meses, abre todo el año.</span>
              <button type="button" className="doc-btn-sec" onClick={onVerAnio}>Ver todo {anio} →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ───────────── Documentos del cliente por año (pestaña de la ficha) ─────────────

export function DocumentosAnio({ cliente, anioInicial, api }) {
  const hoy = new Date();
  const anioActual = hoy.getFullYear();
  const [anio, setAnio] = useState(anioInicial);
  const [archivos, setArchivos] = useState(null);
  const [tipo, setTipo] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  const cargar = useCallback(async () => {
    try {
      setArchivos(await api.listar(cliente.id, { anio }) || []);
    } catch (err) {
      setArchivos([]);
      avisarError('cargar los documentos', err);
    }
  }, [api, cliente.id, anio]);

  useEffect(() => { setArchivos(null); cargar(); }, [cargar]);

  const delAnio = archivos || [];
  const cuenta = (clave) => delAnio.filter(a => clave === 'todos' || tipoDeArchivo(a) === clave).length;
  const tiposConArchivos = TIPOS_DOCUMENTO.filter(t => cuenta(t.clave) > 0);
  // Si el tipo elegido ya no tiene archivos (cambió el año), vuelve a Todos.
  const tipoVigente = tipo === 'todos' || tiposConArchivos.some(t => t.clave === tipo) ? tipo : 'todos';
  const visibles = filtrarArchivos(delAnio, { tipo: tipoVigente, busqueda });
  const ultimoMes = anio === anioActual ? hoy.getMonth() + 1 : 12;
  const meses = [];
  for (let m = ultimoMes; m >= 1; m--) {
    const deMes = visibles.filter(a => Number(a.mes) === m);
    if (busqueda.trim() && !deMes.length) continue;
    meses.push({ mes: m, archivos: deMes });
  }

  return (
    <div className="doc-anio">
      <div className="doc-herramientas">
        <span className="doc-selector-anio">
          <BotonIcono etiqueta="Año anterior" disabled={anio <= ANIO_MINIMO} onClick={() => setAnio(a => a - 1)}>
            <ChevronLeft size={16} aria-hidden="true" />
          </BotonIcono>
          <b>{anio}</b>
          <BotonIcono etiqueta="Año siguiente" disabled={anio >= anioActual} onClick={() => setAnio(a => a + 1)}>
            <ChevronRight size={16} aria-hidden="true" />
          </BotonIcono>
        </span>
        <label className="doc-buscar">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Buscar por nombre de archivo"
            aria-label="Buscar por nombre de archivo"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </label>
      </div>

      <div className="doc-chips" role="group" aria-label="Filtrar por tipo">
        {[{ clave: 'todos', nombre: 'Todos' }, ...tiposConArchivos].map(t => (
          <button
            key={t.clave}
            type="button"
            className="doc-chip"
            aria-pressed={tipoVigente === t.clave}
            onClick={() => setTipo(t.clave)}
          >
            {t.nombre} <span className="doc-cuenta">{cuenta(t.clave)}</span>
          </button>
        ))}
      </div>

      {archivos === null ? (
        <p className="doc-vacio">Cargando…</p>
      ) : (
        <>
          <p className="doc-resultado" role="status">
            {tipoVigente !== 'todos' && `${nombreTipo(tipoVigente)}: `}
            {textoArchivos(visibles.length).replace('Sin archivos', '0 archivos')} en {anio}
            {busqueda.trim() && ` que coinciden con "${busqueda.trim()}"`}
          </p>
          {visibles.length === 0 ? (
            <div className="doc-vacio">
              {delAnio.length === 0 ? `No hay archivos de ${anio}.` : 'Ningún archivo coincide.'}
              {tipoVigente !== 'todos' && busqueda.trim() && (
                <button type="button" className="doc-ver-mas" onClick={() => setTipo('todos')}>
                  Buscar en todos los tipos
                </button>
              )}
            </div>
          ) : (
            <div className="doc-meses">
              {meses.map(({ mes, archivos: deMes }) => (
                <div className="doc-mes" key={mes}>
                  <b>{MESES[mes - 1]}</b>
                  {deMes.length ? (
                    <div>
                      {deMes.map(a => (
                        <ArchivoFila key={a.id} archivo={a} api={api} conTipo={tipoVigente === 'todos'} onCambio={cargar} />
                      ))}
                    </div>
                  ) : <span className="doc-nada">—</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
